import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, CheckCircle, Save, ArrowLeft, DollarSign, FileMinus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { addDaysISO, formatDate, formatMoney, todayISO } from '../lib/format';
import { calcLine, postBill, postCreditNotePurchase, r2 } from '../lib/posting';
import type { Account, Bill, BillLine, Contact, TaxCode, Payment } from '../lib/types';
import PaymentModal from '../components/PaymentModal';

interface Line {
  id: string;
  description: string;
  qty: number;
  unit_price: number;
  tax_code_id: string | null;
  account_id: string | null;
  position: number;
}

const newLine = (pos: number): Line => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  description: '',
  qty: 1,
  unit_price: 0,
  tax_code_id: null,
  account_id: null,
  position: pos,
});

export default function BillEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const isNew = !id;

  const [bill, setBill] = useState<Bill | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);

  const [contactId, setContactId] = useState('');
  const [supplierInvNum, setSupplierInvNum] = useState('');
  const [internalRef, setInternalRef] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(addDaysISO(todayISO(), 30));
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [lines, setLines] = useState<Line[]>([newLine(0)]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [creditingNote, setCreditingNote] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const [cs, accs, tcs] = await Promise.all([
        supabase.from('dodobooks_contacts').select('*').eq('org_id', currentOrg.id).eq('is_supplier', true).eq('is_archived', false).order('name'),
        supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('code'),
        supabase.from('dodobooks_tax_codes').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('code'),
      ]);
      setContacts((cs.data ?? []) as Contact[]);
      setAccounts((accs.data ?? []) as Account[]);
      setTaxCodes((tcs.data ?? []) as TaxCode[]);

      const defTax = (tcs.data ?? []).find((t: any) => t.code === (currentOrg.is_vat_registered ? 'STD' : 'OUT'));
      const defAcc = (accs.data ?? []).find((a: any) => a.code === '6900');
      if (isNew) {
        setLines([{ ...newLine(0), tax_code_id: defTax?.id ?? null, account_id: defAcc?.id ?? null }]);
      }
    })();
  }, [currentOrg, isNew]);

  useEffect(() => {
    if (!id || !currentOrg) return;
    (async () => {
      const { data: b } = await supabase.from('dodobooks_bills').select('*').eq('id', id).maybeSingle();
      if (b) {
        setBill(b as Bill);
        const bb = b as any;
        setContactId(bb.contact_id);
        setSupplierInvNum(bb.supplier_invoice_number ?? '');
        setInternalRef(bb.internal_ref ?? '');
        setIssueDate(bb.issue_date);
        setDueDate(bb.due_date);
        setNotes(bb.notes ?? '');
        setCurrency(bb.currency);
      }
      const { data: ls } = await supabase.from('dodobooks_bill_lines').select('*').eq('bill_id', id).order('position');
      if (ls?.length) {
        setLines(ls.map((l: any) => ({
          id: l.id, description: l.description, qty: Number(l.qty), unit_price: Number(l.unit_price),
          tax_code_id: l.tax_code_id, account_id: l.account_id, position: l.position,
        })));
      }
      const { data: pays } = await supabase
        .from('dodobooks_payments')
        .select('*')
        .eq('bill_id', id)
        .order('payment_date', { ascending: false });
      setPayments((pays ?? []) as Payment[]);
    })();
  }, [id, currentOrg]);

  async function refreshAfterPosting() {
    if (!id) return;
    const { data } = await supabase.from('dodobooks_bills').select('*').eq('id', id).maybeSingle();
    if (data) setBill(data as Bill);
    const { data: pays } = await supabase
      .from('dodobooks_payments')
      .select('*')
      .eq('bill_id', id)
      .order('payment_date', { ascending: false });
    setPayments((pays ?? []) as Payment[]);
  }

  async function issueCreditNote() {
    if (!currentOrg || !bill) return;
    if (!confirm(`Issue a supplier credit note for the outstanding balance? This reverses the bill's expense and VAT on the ledger.`)) return;
    setCreditingNote(true); setErr(null); setSavedMsg(null);

    const { count } = await supabase
      .from('dodobooks_bills')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', currentOrg.id)
      .eq('kind', 'credit_note');
    const ref = `SCN-${new Date().getFullYear()}-${String((count ?? 0) + 1).padStart(4, '0')}`;
    const outstanding = r2(Number(bill.total) - Number(bill.amount_paid));

    const { data: cn, error: ce } = await supabase
      .from('dodobooks_bills')
      .insert({
        org_id: currentOrg.id,
        contact_id: bill.contact_id,
        supplier_invoice_number: ref,
        internal_ref: `Credit note for ${bill.supplier_invoice_number ?? bill.internal_ref ?? bill.id.slice(0, 8)}`,
        issue_date: todayISO(),
        due_date: todayISO(),
        status: 'draft',
        currency: bill.currency,
        subtotal: 0, tax_total: 0, total: 0, amount_paid: 0,
        kind: 'credit_note',
        applied_to_bill_id: bill.id,
        notes: `Supplier credit note. Outstanding ${formatMoney(outstanding, bill.currency)}.`,
      })
      .select('*')
      .single();
    if (ce || !cn) { setCreditingNote(false); setErr(ce?.message ?? 'Failed to create credit note'); return; }

    const cnLines = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return {
        bill_id: (cn as any).id,
        description: l.description,
        qty: l.qty || 0,
        unit_price: l.unit_price || 0,
        tax_code_id: l.tax_code_id,
        account_id: l.account_id,
        line_total: r2(lineTotal),
        position: i,
      };
    });
    if (cnLines.length > 0) await supabase.from('dodobooks_bill_lines').insert(cnLines);

    const { data: cnFresh } = await supabase.from('dodobooks_bills').select('*').eq('id', (cn as any).id).single();
    const { data: cnLineRows } = await supabase.from('dodobooks_bill_lines').select('*').eq('bill_id', (cn as any).id).order('position');
    const result = await postCreditNotePurchase({
      org_id: currentOrg.id,
      bill: cnFresh as Bill,
      lines: (cnLineRows ?? []) as BillLine[],
      accounts,
      taxCodes,
      applies_to_bill: bill,
    });
    setCreditingNote(false);
    if (result.error) { setErr(result.error); return; }
    setSavedMsg(`Supplier credit note ${ref} issued.`);
    refreshAfterPosting();
    setTimeout(() => navigate(`/bills/${(cn as any).id}`), 800);
  }

  const status = bill?.status ?? 'draft';
  const isCreditNote = bill?.kind === 'credit_note';
  const readOnly = status !== 'draft';
  const outstanding = bill ? r2(Number(bill.total) - Number(bill.amount_paid)) : 0;
  const canRecordPayment = !isCreditNote && readOnly && status !== 'paid' && status !== 'void' && outstanding > 0.005;
  const canIssueCreditNote = !isCreditNote && readOnly && status !== 'void' && outstanding > 0.005;

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const l of lines) {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const rate = tc?.rate ?? 0;
      const { net, tax: lineTax } = calcLine(l.qty || 0, l.unit_price || 0, rate);
      subtotal += net; tax += lineTax;
    }
    return { subtotal: r2(subtotal), tax: r2(tax), total: r2(subtotal + tax) };
  }, [lines, taxCodes]);

  const updateLine = (i: number, patch: Partial<Line>) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i: number) => setLines((p) => p.length === 1 ? p : p.filter((_, idx) => idx !== i));
  const addLine = () => {
    const last = lines[lines.length - 1];
    setLines((p) => [...p, { ...newLine(p.length), tax_code_id: last?.tax_code_id ?? null, account_id: last?.account_id ?? null }]);
  };

  async function persistDraft(): Promise<string | null> {
    if (!currentOrg) return null;
    if (!contactId) { setErr('Pick a supplier first.'); return null; }
    const payload = {
      org_id: currentOrg.id,
      contact_id: contactId,
      supplier_invoice_number: supplierInvNum || null,
      internal_ref: internalRef || null,
      issue_date: issueDate,
      due_date: dueDate,
      status: status === 'draft' ? 'draft' : status,
      currency,
      subtotal: totals.subtotal,
      tax_total: totals.tax,
      total: totals.total,
      notes: notes || null,
    };
    let billId = id ?? null;
    if (billId) {
      const { error } = await supabase.from('dodobooks_bills').update(payload).eq('id', billId);
      if (error) { setErr(error.message); return null; }
    } else {
      const { data, error } = await supabase.from('dodobooks_bills').insert(payload).select('id').single();
      if (error || !data) { setErr(error?.message ?? 'Insert failed'); return null; }
      billId = data.id;
    }
    await supabase.from('dodobooks_bill_lines').delete().eq('bill_id', billId);
    const lineRows = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return {
        bill_id: billId,
        description: l.description,
        qty: l.qty || 0,
        unit_price: l.unit_price || 0,
        tax_code_id: l.tax_code_id,
        account_id: l.account_id,
        line_total: r2(lineTotal),
        position: i,
      };
    });
    if (lineRows.length > 0) {
      const { error } = await supabase.from('dodobooks_bill_lines').insert(lineRows);
      if (error) { setErr(error.message); return null; }
    }
    return billId;
  }

  async function onSave() {
    setBusy(true); setErr(null); setSavedMsg(null);
    const newId = await persistDraft();
    setBusy(false);
    if (newId) { setSavedMsg('Draft saved.'); if (!id) navigate(`/bills/${newId}`, { replace: true }); }
  }

  async function onApprove() {
    if (!currentOrg) return;
    setBusy(true); setErr(null); setSavedMsg(null);
    const newId = await persistDraft();
    if (!newId) { setBusy(false); return; }
    const { data: bFresh } = await supabase.from('dodobooks_bills').select('*').eq('id', newId).maybeSingle();
    const { data: linesFresh } = await supabase.from('dodobooks_bill_lines').select('*').eq('bill_id', newId).order('position');
    if (!bFresh || !linesFresh) { setBusy(false); setErr('Could not reload after save.'); return; }
    const result = await postBill({
      org_id: currentOrg.id,
      bill: bFresh as Bill,
      lines: linesFresh as BillLine[],
      accounts,
      taxCodes,
    });
    setBusy(false);
    if (result.error) setErr(result.error);
    else {
      setSavedMsg('Bill approved and posted.');
      if (!id) navigate(`/bills/${newId}`, { replace: true });
      else {
        const { data } = await supabase.from('dodobooks_bills').select('*').eq('id', newId).maybeSingle();
        setBill(data as Bill);
      }
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/bills" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
          <div>
            <h1 className="h-page">
            {isCreditNote && <span className="font-mono text-xs uppercase tracking-widest text-gold mr-2 align-middle">Credit note</span>}
            {supplierInvNum || 'New bill'}
          </h1>
            <p className="text-text-dim text-sm mt-1">
              {readOnly ? <span className={`pill pill-${status === 'approved' ? 'sent' : status}`}>{status}</span> : 'Editing draft'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && (
            <>
              <button onClick={onSave} disabled={busy} className="btn btn-secondary disabled:opacity-50"><Save size={14} /> Save draft</button>
              <button onClick={onApprove} disabled={busy} className="btn btn-primary disabled:opacity-50"><CheckCircle size={14} /> Approve & post</button>
            </>
          )}
          {canRecordPayment && (
            <button onClick={() => setShowPayment(true)} className="btn btn-primary"><DollarSign size={14} /> Record payment</button>
          )}
          {canIssueCreditNote && (
            <button onClick={issueCreditNote} disabled={creditingNote} className="btn btn-secondary disabled:opacity-50">
              <FileMinus size={14} /> {creditingNote ? 'Issuing…' : 'Issue credit note'}
            </button>
          )}
        </div>
      </header>

      {savedMsg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{savedMsg}</div>}
      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

      <div className="card grid grid-cols-2 gap-4">
        <div className="field col-span-2">
          <label>Supplier *</label>
          <select disabled={readOnly} required value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">— pick a supplier —</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Supplier invoice no.</label><input disabled={readOnly} value={supplierInvNum} onChange={(e) => setSupplierInvNum(e.target.value)} /></div>
        <div className="field"><label>Internal ref</label><input disabled={readOnly} value={internalRef} onChange={(e) => setInternalRef(e.target.value)} /></div>
        <div className="field"><label>Issue date</label><input disabled={readOnly} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></div>
        <div className="field"><label>Due date</label><input disabled={readOnly} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
        <div className="field"><label>Currency</label>
          <select disabled={readOnly} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option>ZAR</option><option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table>
          <thead>
            <tr>
              <th className="px-4">Description</th>
              <th className="w-20">Qty</th>
              <th className="w-32">Unit price</th>
              <th className="w-56">Account</th>
              <th className="w-32">Tax</th>
              <th className="w-32 text-right">Line total</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const tc = taxCodes.find((t) => t.id === l.tax_code_id);
              const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
              return (
                <tr key={l.id}>
                  <td className="px-4"><input disabled={readOnly} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="w-full" /></td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.qty} onChange={(e) => updateLine(i, { qty: Number(e.target.value) })} className="w-full" /></td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} className="w-full" /></td>
                  <td>
                    <select disabled={readOnly} value={l.account_id ?? ''} onChange={(e) => updateLine(i, { account_id: e.target.value || null })} className="w-full">
                      <option value="">— pick account —</option>
                      {accounts.filter((a) => a.account_type === 'expense' || a.account_type === 'cogs' || a.account_type === 'asset').map((a) => (
                        <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select disabled={readOnly} value={l.tax_code_id ?? ''} onChange={(e) => updateLine(i, { tax_code_id: e.target.value || null })} className="w-full">
                      <option value="">No VAT</option>
                      {taxCodes.filter((t) => t.is_input).map((t) => (
                        <option key={t.id} value={t.id}>{t.code} ({(t.rate * 100).toFixed(0)}%)</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-right num">{formatMoney(lineTotal, currency)}</td>
                  <td>
                    {!readOnly && (
                      <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} className="text-text-mute hover:text-rust disabled:opacity-30">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (
          <div className="border-t border-line p-3">
            <button type="button" onClick={addLine} className="btn btn-ghost"><Plus size={14} /> Add line</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <label className="block mb-1">Notes</label>
          <textarea disabled={readOnly} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" />
        </div>
        <div className="card">
          <div className="flex justify-between py-1.5 text-sm text-text-dim"><span>Subtotal</span><span className="num text-text">{formatMoney(totals.subtotal, currency)}</span></div>
          <div className="flex justify-between py-1.5 text-sm text-text-dim border-b border-line"><span>VAT</span><span className="num text-text">{formatMoney(totals.tax, currency)}</span></div>
          <div className="flex justify-between py-2 font-serif text-lg"><span>Total</span><span className="num text-emerald">{formatMoney(totals.total, currency)}</span></div>
        </div>
      </div>

      {payments.length > 0 && (
        <div className="card p-0">
          <div className="px-5 py-3 border-b border-line"><h3 className="h-section text-base">Payments made</h3></div>
          <table>
            <thead>
              <tr><th className="px-5">Date</th><th>Reference</th><th className="text-right pr-5">Amount</th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td className="px-5">{formatDate(p.payment_date)}</td>
                  <td className="text-text-dim">{p.reference || '—'}</td>
                  <td className="text-right num pr-5 text-rust">{formatMoney(p.amount, bill?.currency ?? 'ZAR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bill?.journal_id && (
        <p className="text-xs text-text-mute font-mono">
          Posted to journal {bill.journal_id.slice(0, 8)} on {formatDate(bill.issue_date)} · source={isCreditNote ? 'supplier-credit-note' : 'bill'}
        </p>
      )}

      {showPayment && bill && (
        <PaymentModal
          direction="made"
          bill={bill}
          onClose={() => setShowPayment(false)}
          onPosted={() => { setShowPayment(false); setSavedMsg('Payment recorded and posted.'); refreshAfterPosting(); }}
        />
      )}
    </div>
  );
}
